import { Injectable, OnDestroy } from '@angular/core';
import { Subscription, Observable, BehaviorSubject, firstValueFrom, take, map, from, shareReplay, of, defer, filter, timeout } from 'rxjs';
import { collection, collectionData, Firestore, getDocs, orderBy, query } from '@angular/fire/firestore';

import algoliasearch from 'algoliasearch';

import { SearchPageResult, SearchResult, SearchResults } from '../models/search-result.model';
import { environment } from 'src/environments/environment';
import { AdminSettingsService } from './admin-settings.service';
import { AnalyticsService } from './analytics.service';
import { UserReference } from '../models/user.model';
import { Group } from '../models/group.model';
import { UserRolesService } from './user-roles.service';
import { escapeRegExp, formatDisplayName, getFullName, nameMatchScore } from 'src/app/util/string';
import { UserPhotoLookupService } from './user-photo-lookup.service';

const NUM_RESULTS = 10;
const PAGE_RESULTS = 25;

@Injectable({
  providedIn: 'root'
})
export class SearchService implements OnDestroy {
  private ignoreNextChange = false;
  private query$ = new BehaviorSubject<string | null>(null);
  private results$ = new BehaviorSubject<SearchResults | null>(null);
  private agents$ = new Observable<UserReference[] | undefined>();
  private offices$ = new Observable<Group[] | undefined>();
  private searchSubscription: Subscription;

  constructor(
    private firestore: Firestore,
    private userRoles: UserRolesService,
    private adminSettingsService: AdminSettingsService,
    private userPhotoLookupService: UserPhotoLookupService,
    private analyticsService: AnalyticsService,
  ) {
    this.searchSubscription = this.query$
      .subscribe((query) => {
        if (!this.ignoreNextChange && query) {
          this.search(query);
        }

        this.ignoreNextChange = false;
      });
  }

  public async fetchPageResults(query: string, page = 0): Promise<SearchPageResult | null> {
    if (!query) return null;
    if (this.userRoles.isAdmin) {
      return this.adminFetchPage(query, page, page, page);
    }
    return this.groupFetchPage(query);
  }

  // 'groups' searches the group index unfiltered, for callers that want offices
  // and franchisors together.
  public async fetchCategoryResults(
    query: string,
    kind: 'agents' | 'offices' | 'franchisors' | 'groups',
    page: number,
  ): Promise<{ results: SearchResult[]; nbPages: number } | null> {
    if (!query || !this.userRoles.isAdmin) return null;

    const settings = this.adminSettingsService.getSettings();
    if (!settings?.searchAppId || !settings?.searchApiKey) return null;

    const client = algoliasearch(settings.searchAppId, settings.searchApiKey);
    const indexName = kind === 'agents' ? environment.searchIndexes.agents : environment.searchIndexes.groups;
    const filters = kind === 'offices' ? 'isSuperGroup:false' : kind === 'franchisors' ? 'isSuperGroup:true' : undefined;

    try {
      const { results }: any = await client.multipleQueries([{
        indexName,
        query,
        params: { hitsPerPage: PAGE_RESULTS, page, ...(filters ? { filters } : {}) },
      }]);

      const hits = results[0].hits as SearchResult[];
      const resolvedResults = kind === 'agents'
        ? await this.attachSearchPhotoUrls(hits)
        : hits;

      return { results: resolvedResults, nbPages: results[0].nbPages as number };
    } catch (err) {
      console.error('[SearchService] fetchCategoryResults error', err);
      return null;
    }
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  public async search(query: string) {
    if (!query) {
      return this.reset();
    }

    const index = this.userRoles.isAdmin ? 'algolia' : 'local';
    this.analyticsService.logSearch(index);

    if (this.userRoles.isAdmin) {
      this.performAdminSearch(query);
    } else {
      this.performGroupSearch(query);
    }
  }

  public getQuery(): Observable<string | null> {
    return this.query$.asObservable();
  }

  public setQuery(value: string) {
    this.query$.next(value);
  }

  public getResults$(): Observable<SearchResults | null> {
    return this.results$.asObservable();
  }

  public reset() {
    this.ignoreNextChange = true;
    this.query$.next(null);
    this.results$.next(null);
  }

  private applyCategoryOrder(searchResults: SearchResults, query: string) {
    const scoreFor = (results?: SearchResult[]) =>
      results?.length ? Math.max(...results.map(r => nameMatchScore(r.name, query))) : -1;

    const scores: Record<'agents' | 'offices' | 'franchisors', number> = {
      agents: scoreFor(searchResults.agents),
      offices: scoreFor(searchResults.offices),
      franchisors: scoreFor(searchResults.franchisors),
    };

    searchResults.categoryOrder = (['agents', 'offices', 'franchisors'] as const)
      .slice()
      .sort((a, b) => scores[b] - scores[a]);
  }

  public async setDataPipe(groupId?: string | null, isSuperGroup?: boolean) {
    if (this.userRoles.isAdmin) {
      return;
    }

    this.agents$ = of<UserReference[]>([]);
    this.offices$ = of<Group[]>([]);

    if (!groupId) {
      return;
    }

    const qMembers = query(
      collection(this.firestore, `groups/${groupId}/members`),
      orderBy('firstName'), orderBy('lastName')
    );

    this.agents$ = defer(() => getDocs(qMembers)).pipe(
      map(snap => snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }))),
      shareReplay(1)
    );

    if (isSuperGroup) {
      const qGroups = query(collection(this.firestore, `groups/${groupId}/groups`), orderBy('name', 'asc'));
      this.offices$ = defer(() => getDocs(qGroups)).pipe(
        map(snap => snap.docs
          .map(d => ({ id: d.id, ...(d.data() as any) }))
          .filter(group => group.isArchived !== true)),
        shareReplay(1)
      );
    }
  }

  private performAdminSearch(query: string) {
    const searchQueries: Array<{
      kind: 'agents' | 'offices' | 'franchisors';
      indexName: string;
      query: string;
      params: Record<string, unknown>;
    }> = [
      {
        kind: 'agents',
        indexName: environment.searchIndexes.agents,
        query,
        params: {
          hitsPerPage: NUM_RESULTS,
        }
      },
      {
        kind: 'offices',
        indexName: environment.searchIndexes.groups,
        query,
        params: {
          hitsPerPage: NUM_RESULTS,
          filters: 'isSuperGroup:false',
        }
      },
      {
        kind: 'franchisors',
        indexName: environment.searchIndexes.groups,
        query,
        params: {
          hitsPerPage: NUM_RESULTS,
          filters: 'isSuperGroup:true',
        }
      }
    ];

    const settings = this.adminSettingsService.getSettings();

    if (!settings || (settings && (!settings.searchAppId || !settings.searchApiKey))) {
      return this.reset();
    }

    const client = algoliasearch(settings.searchAppId, settings.searchApiKey);

    client.multipleQueries(searchQueries.map(({ kind, ...searchQuery }) => searchQuery))
      .then(async ({ results }: any) => {
        const searchResults = new SearchResults;

        for (let index = 0; index < results.length; index++) {
          const resultSet = results[index];
          const kind = searchQueries[index]?.kind;
          const rawItems = resultSet.hits as SearchResult[];
          const items = kind === 'agents'
            ? await this.attachSearchPhotoUrls(rawItems)
            : rawItems;
          searchResults.totalCount += items.length;

          switch (kind) {
            case 'agents':
              searchResults.agents = items;
              searchResults.agentCount = items.length;
              break;

            case 'offices':
              searchResults.offices = items;
              searchResults.officeCount = items.length;
              break;

            case 'franchisors':
              searchResults.franchisors = items;
              searchResults.franchisorCount = items.length;
              break;
          }
        }

        this.applyCategoryOrder(searchResults, query);
        this.results$.next(searchResults);
      })
      .catch((err: any) => {
        console.error('[SearchService] performAdminSearch error', err);
        this.results$.next(new SearchResults());
      });
  }

  private async adminFetchPage(
    query: string,
    agentPage: number,
    officePage: number,
    franchisorPage: number,
  ): Promise<SearchPageResult | null> {
    let settings = this.adminSettingsService.getSettings();
    if (!settings?.searchAppId || !settings?.searchApiKey) {
      try {
        settings = await firstValueFrom(
          this.adminSettingsService.getSettings$().pipe(
            filter(s => !!s?.searchAppId && !!s?.searchApiKey),
            timeout(5000),
          )
        );
      } catch {
        return null;
      }
    }
    if (!settings?.searchAppId || !settings?.searchApiKey) return null;

    const client = algoliasearch(settings.searchAppId, settings.searchApiKey);

    const queries = [
      { indexName: environment.searchIndexes.agents, query, params: { hitsPerPage: PAGE_RESULTS, page: agentPage } },
      { indexName: environment.searchIndexes.groups, query, params: { hitsPerPage: PAGE_RESULTS, page: officePage, filters: 'isSuperGroup:false' } },
      { indexName: environment.searchIndexes.groups, query, params: { hitsPerPage: PAGE_RESULTS, page: franchisorPage, filters: 'isSuperGroup:true' } },
    ];

    try {
      const { results }: any = await client.multipleQueries(queries);
      const [a, o, f] = results;

      return {
        agents: await this.attachSearchPhotoUrls(a.hits as SearchResult[]),
        agentTotal: a.nbHits as number,
        agentNbPages: a.nbPages as number,
        offices: o.hits as SearchResult[],
        officeTotal: o.nbHits as number,
        officeNbPages: o.nbPages as number,
        franchisors: f.hits as SearchResult[],
        franchisorTotal: f.nbHits as number,
        franchisorNbPages: f.nbPages as number,
      };
    } catch (err) {
      console.error('[SearchService] adminFetchPage error', err);
      return null;
    }
  }

  private async groupFetchPage(query: string): Promise<SearchPageResult> {
    const agents = await firstValueFrom(this.agents$);
    const offices = await firstValueFrom(this.offices$);
    const re = new RegExp(escapeRegExp(query), 'i');

    const matchedAgents: SearchResult[] = (agents ?? [])
      .filter(x =>
        re.test(getFullName(x.firstName, x.lastName))
        || (x.nickname && re.test(x.nickname))
        || x.licenceNumbers?.includes(query)
      )
      .map(x => ({
        objectID: x.id,
        name: formatDisplayName(x.firstName, x.lastName, x.nickname),
        firstName: x.firstName,
        lastName: x.lastName,
        nickname: x.nickname,
        group: x.group,
        licenceNumbers: x.licenceNumbers,
        licences: x.licences,
      }));

    const matchedOffices: SearchResult[] = (offices ?? [])
      .filter(x => !x.isSuperGroup && re.test(x.name ?? ''))
      .map(x => ({ objectID: x.id, name: x.name, isSuperGroup: false }));

    return {
      agents: await this.attachSearchPhotoUrls(matchedAgents),
      agentTotal: matchedAgents.length,
      agentNbPages: 1,
      offices: matchedOffices,
      officeTotal: matchedOffices.length,
      officeNbPages: 1,
      franchisors: [],
      franchisorTotal: 0,
      franchisorNbPages: 1,
    };
  }

  private async performGroupSearch(query: string) {
    const searchResults = new SearchResults;

    let agents: UserReference[] | undefined;
    let offices: Group[] | undefined;
    try {
      agents = await firstValueFrom(this.agents$);
      offices = await firstValueFrom(this.offices$);
    } catch (err) {
      console.error('[SearchService] performGroupSearch error', err);
      this.results$.next(searchResults);
      return;
    }

    const foundAgents = agents?.filter(x => {
      const re = new RegExp(escapeRegExp(query), 'i');
      return re.test(getFullName(x.firstName, x.lastName))
        || (x.nickname && re.test(x.nickname))
        || (x.licenceNumbers && x.licenceNumbers?.includes(query));
    });
    const foundOffices = offices?.filter(x => new RegExp(escapeRegExp(query), 'i').test(x.name ?? ''));

    if (foundAgents) {
      searchResults.agents = Array<SearchResult>();

      const agentResults = foundAgents.map((result: UserReference) => ({
          objectID: result.id,
          name: formatDisplayName(result.firstName, result.lastName, result.nickname),
          firstName: result.firstName,
          lastName: result.lastName,
          nickname: result.nickname,
          group: result.group,
          licenceNumbers: result.licenceNumbers,
          licences: result.licences,
      }));

      searchResults.agents = await this.attachSearchPhotoUrls(agentResults);

      searchResults.agents.forEach(() => {
        searchResults.agentCount++;
        searchResults.totalCount++;
      });
    }

    if (foundOffices) {
      searchResults.offices = Array<SearchResult>();

      foundOffices.forEach((result: Group) => {
        if (!result.isSuperGroup) {
          searchResults.offices?.push({
            objectID: result.id,
            name: result.name,
            isSuperGroup: false,
          });

          searchResults.officeCount++;
          searchResults.totalCount++;
        }
      });
    }

    this.applyCategoryOrder(searchResults, query);
    this.results$.next(searchResults);
  }

  private async attachSearchPhotoUrls(results: SearchResult[]): Promise<SearchResult[]> {
    if (!results.length) {
      return results;
    }

    const normalizedResults = results.map(result => ({ ...result, photoUrl: undefined }));

    try {
      const photoUrls = await firstValueFrom(this.userPhotoLookupService.getPhotoUrls$(normalizedResults.map(result => result.objectID)));
      return normalizedResults.map(result => {
        if (!result.objectID || !photoUrls.has(result.objectID)) {
          return result;
        }

        return {
          ...result,
          photoUrl: photoUrls.get(result.objectID) ?? undefined,
        };
      });
    } catch (error) {
      console.error('[SearchService] failed to resolve agent photos', error);
      return normalizedResults;
    }
  }
}
